import { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import glowUrl from "../assets/glow.mp3";

type BrowserAudioWindow = Window & {
  webkitAudioContext?: typeof AudioContext;
};

type LabSource = AudioScheduledSourceNode & {
  stop: (when?: number) => void;
};

function createSoftCurve(length: number, startValue: number, endValue: number) {
  const curve = new Float32Array(length);

  for (let i = 0; i < length; i += 1) {
    const progress = i / Math.max(1, length - 1);
    const eased = progress * progress * (3 - 2 * progress);
    curve[i] = startValue + (endValue - startValue) * eased;
  }

  return curve;
}

export function WebAudioLab() {
  const audioContextRef = useRef<AudioContext | null>(null);
  const glowBufferRef = useRef<AudioBuffer | null>(null);
  const activeSourcesRef = useRef<Set<LabSource>>(new Set());
  const timerTimeoutRef = useRef<number | null>(null);
  const timerRunIdRef = useRef(0);
  const audioRunInFlightRef = useRef(false);
  const fireCountRef = useRef(0);

  const [status, setStatus] = useState("Timer is stopped.");
  const [fadeInSeconds, setFadeInSeconds] = useState(6);
  const [startGain, setStartGain] = useState(0.002);
  const [targetGain, setTargetGain] = useState(0.16);
  const [playSeconds, setPlaySeconds] = useState(7);
  const [glowOffset, setGlowOffset] = useState(0);
  const [lowPassFrequency, setLowPassFrequency] = useState(4200);
  const [timerMinutes, setTimerMinutes] = useState("5");
  const [timerRunning, setTimerRunning] = useState(false);
  const [fireCount, setFireCount] = useState(0);
  const [useLimiter, setUseLimiter] = useState(true);

  const getAudioContext = useCallback(async () => {
    const AudioContextClass =
      window.AudioContext ?? (window as BrowserAudioWindow).webkitAudioContext;

    if (!AudioContextClass) {
      setStatus("This browser does not expose AudioContext.");
      return null;
    }

    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContextClass();
    }

    if (audioContextRef.current.state === "suspended") {
      await audioContextRef.current.resume();
    }

    return audioContextRef.current;
  }, []);

  const loadGlowBuffer = useCallback(async (context: AudioContext) => {
    if (glowBufferRef.current) {
      return glowBufferRef.current;
    }

    const response = await fetch(glowUrl);
    const arrayBuffer = await response.arrayBuffer();
    const audioBuffer = await context.decodeAudioData(arrayBuffer);
    glowBufferRef.current = audioBuffer;
    return audioBuffer;
  }, []);

  const trackSource = useCallback((source: LabSource) => {
    activeSourcesRef.current.add(source);
    source.addEventListener(
      "ended",
      () => {
        activeSourcesRef.current.delete(source);
      },
      { once: true },
    );
  }, []);

  const createOutput = useCallback(
    (context: AudioContext) => {
      const gain = context.createGain();
      gain.gain.value = 0;

      if (!useLimiter) {
        gain.connect(context.destination);
        return gain;
      }

      const compressor = context.createDynamicsCompressor();
      compressor.threshold.value = -30;
      compressor.knee.value = 24;
      compressor.ratio.value = 8;
      compressor.attack.value = 0.005;
      compressor.release.value = 0.18;
      gain.connect(compressor);
      compressor.connect(context.destination);
      return gain;
    },
    [useLimiter],
  );

  const scheduleGlowGain = useCallback(
    (gainNode: GainNode, context: AudioContext, actualPlaySeconds: number) => {
      const now = context.currentTime;
      const safeStart = Math.max(0.0001, startGain);
      const safeTarget = Math.max(safeStart, targetGain);
      const safeFadeIn = Math.max(0.05, fadeInSeconds);
      const fadeOutStart = now + Math.max(safeFadeIn, actualPlaySeconds - 0.75);

      gainNode.gain.cancelScheduledValues(now);
      gainNode.gain.setValueAtTime(safeStart, now);
      gainNode.gain.setValueCurveAtTime(
        createSoftCurve(96, safeStart, safeTarget),
        now,
        Math.min(safeFadeIn, actualPlaySeconds),
      );
      gainNode.gain.setTargetAtTime(0.0001, fadeOutStart, 0.35);
    },
    [fadeInSeconds, startGain, targetGain],
  );

  const playGlowAudio = useCallback(async () => {
    const context = await getAudioContext();

    if (!context) {
      return false;
    }

    const buffer = await loadGlowBuffer(context);
    const now = context.currentTime;
    const source = context.createBufferSource();
    const output = createOutput(context);
    const filter = context.createBiquadFilter();
    const safeOffset = Math.min(glowOffset, buffer.duration - 0.1);
    const actualPlaySeconds = Math.min(
      playSeconds,
      Math.max(0.4, buffer.duration - safeOffset),
    );

    source.buffer = buffer;
    filter.type = "lowpass";
    filter.Q.value = 0.42;
    filter.frequency.setValueAtTime(lowPassFrequency, now);

    source.connect(filter);
    filter.connect(output);
    scheduleGlowGain(output, context, actualPlaySeconds);
    source.start(now, safeOffset, actualPlaySeconds);
    source.stop(now + actualPlaySeconds + 0.1);
    trackSource(source);
    return true;
  }, [
    createOutput,
    getAudioContext,
    glowOffset,
    loadGlowBuffer,
    lowPassFrequency,
    playSeconds,
    scheduleGlowGain,
    trackSource,
  ]);

  const notifyOnceForAudioRun = useCallback((nextCount: number) => {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification("Glow timer", {
        body: `Soft glow notification fired ${nextCount} total time${
          nextCount === 1 ? "" : "s"
        }.`,
      });
    }
  }, []);

  const requestNotificationPermission = useCallback(async () => {
    if ("Notification" in window && Notification.permission === "default") {
      await Notification.requestPermission();
    }
  }, []);

  const runGlowNotification = useCallback(
    async (runId?: number) => {
      if (runId && runId !== timerRunIdRef.current) {
        return;
      }

      if (audioRunInFlightRef.current) {
        return;
      }

      audioRunInFlightRef.current = true;

      try {
        const didPlay = await playGlowAudio();

        if (!didPlay) {
          return;
        }

        const nextCount = fireCountRef.current + 1;
        fireCountRef.current = nextCount;
        notifyOnceForAudioRun(nextCount);
        setFireCount(nextCount);
        setStatus(
          `Glow notification ran ${nextCount} total time${
            nextCount === 1 ? "" : "s"
          }.`,
        );
      } finally {
        audioRunInFlightRef.current = false;
      }
    },
    [notifyOnceForAudioRun, playGlowAudio],
  );

  const stopTimer = useCallback(() => {
    timerRunIdRef.current += 1;

    if (timerTimeoutRef.current) {
      window.clearTimeout(timerTimeoutRef.current);
      timerTimeoutRef.current = null;
    }

    setTimerRunning(false);
    setStatus(`Timer stopped after ${fireCountRef.current} total fire(s).`);
  }, []);

  const stopAll = useCallback(() => {
    stopTimer();
    activeSourcesRef.current.forEach((source) => {
      try {
        source.stop();
      } catch {
        activeSourcesRef.current.delete(source);
      }
    });
  }, [stopTimer]);

  const startTimer = useCallback(async () => {
    await requestNotificationPermission();

    if (timerTimeoutRef.current) {
      window.clearTimeout(timerTimeoutRef.current);
    }

    const parsedMinutes = Number(timerMinutes);
    const safeMinutes =
      Number.isFinite(parsedMinutes) && parsedMinutes >= 1 ? parsedMinutes : 1;
    const timeoutMs = safeMinutes * 60 * 1000;
    const runId = timerRunIdRef.current + 1;

    timerRunIdRef.current = runId;
    fireCountRef.current = 0;
    setFireCount(0);
    setTimerRunning(true);
    setStatus(`Timer started. It will run every ${safeMinutes} minute(s).`);

    const scheduleNextRun = () => {
      timerTimeoutRef.current = window.setTimeout(() => {
        if (runId !== timerRunIdRef.current) {
          return;
        }

        void runGlowNotification(runId).finally(() => {
          if (runId === timerRunIdRef.current) {
            scheduleNextRun();
          }
        });
      }, timeoutMs);
    };

    scheduleNextRun();
  }, [requestNotificationPermission, runGlowNotification, timerMinutes]);

  const runOnce = useCallback(async () => {
    await requestNotificationPermission();
    await runGlowNotification();
  }, [requestNotificationPermission, runGlowNotification]);

  useEffect(() => {
    return () => {
      stopAll();
      void audioContextRef.current?.close();
      audioContextRef.current = null;
    };
  }, [stopAll]);

  return (
    <Box sx={{ maxWidth: 960, mx: "auto", p: 3, textAlign: "left", color: "white" }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" component="h2" sx={{ color: "white", mb: 1 }}>
            Glow Notification Timer
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.72)", maxWidth: 720 }}>
            Plays glow.mp3 with a soft Web Audio fade-in, then creates one browser
            notification for that audio run.
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: "1px solid rgba(255,255,255,0.14)",
            bgcolor: "rgba(255,255,255,0.06)",
          }}
        >
          <Stack spacing={2.5}>
            <Typography variant="h6" sx={{ color: "white" }}>
              Audio Settings
            </Typography>

            <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
              <Box sx={{ flex: 1 }}>
                <Typography gutterBottom>Fade in: {fadeInSeconds.toFixed(1)}s</Typography>
                <Slider
                  min={1}
                  max={16}
                  step={0.5}
                  value={fadeInSeconds}
                  onChange={(_, value) => setFadeInSeconds(value as number)}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography gutterBottom>Start gain: {startGain.toFixed(4)}</Typography>
                <Slider
                  min={0.0001}
                  max={0.04}
                  step={0.0005}
                  value={startGain}
                  onChange={(_, value) => setStartGain(value as number)}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography gutterBottom>Target gain: {targetGain.toFixed(3)}</Typography>
                <Slider
                  min={0.02}
                  max={0.5}
                  step={0.005}
                  value={targetGain}
                  onChange={(_, value) => setTargetGain(value as number)}
                />
              </Box>
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
              <Box sx={{ flex: 1 }}>
                <Typography gutterBottom>Glow offset: {glowOffset.toFixed(1)}s</Typography>
                <Slider
                  min={0}
                  max={12}
                  step={0.1}
                  value={glowOffset}
                  onChange={(_, value) => setGlowOffset(value as number)}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography gutterBottom>Play length: {playSeconds.toFixed(1)}s</Typography>
                <Slider
                  min={1}
                  max={18}
                  step={0.5}
                  value={playSeconds}
                  onChange={(_, value) => setPlaySeconds(value as number)}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography gutterBottom>
                  Low-pass: {Math.round(lowPassFrequency)}Hz
                </Typography>
                <Slider
                  min={700}
                  max={9000}
                  step={50}
                  value={lowPassFrequency}
                  onChange={(_, value) => setLowPassFrequency(value as number)}
                />
              </Box>
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  checked={useLimiter}
                  onChange={(event) => setUseLimiter(event.target.checked)}
                />
              }
              label="Limiter"
            />
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: "1px solid rgba(255,255,255,0.14)",
            bgcolor: "rgba(255,255,255,0.06)",
          }}
        >
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ color: "white" }}>
              Timer
            </Typography>

            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
              <TextField
                label="Repeat every minutes"
                type="text"
                value={timerMinutes}
                onChange={(event) => {
                  if (/^\d*$/.test(event.target.value)) {
                    setTimerMinutes(event.target.value);
                  }
                }}
                slotProps={{
                  htmlInput: {
                    inputMode: "numeric",
                    min: 1,
                    pattern: "[0-9]*",
                    step: 1,
                  },
                }}
                sx={{ maxWidth: { md: 220 } }}
              />
              <Button variant="contained" onClick={startTimer}>
                Start timer
              </Button>
              <Button variant="outlined" onClick={() => void runOnce()}>
                Run once
              </Button>
              <Button color="error" variant="outlined" onClick={stopAll}>
                Stop
              </Button>
            </Stack>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.12)" }} />

            <Typography sx={{ color: "rgba(255,255,255,0.72)" }}>
              Timer: {timerRunning ? "running" : "stopped"} - fired {fireCount} total
              time{fireCount === 1 ? "" : "s"}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.78)" }}>{status}</Typography>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}
